import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Toaster } from 'sonner';
import { useAuth } from './hooks/useAuth';
import { useTheme } from './hooks/useTheme';
import { Layout } from './components/Layout';
import { SuspensionCheck } from './components/SuspensionCheck';
import { ProxyProvider } from './contexts/ProxyContext';
import { Dashboard } from './pages/Dashboard';
import Campaigns from './pages/Campaigns';
import MetaAlignment from './pages/MetaAlignment';
import { Contacts } from './pages/Contacts';
import { Artists } from './pages/Artists';
import { Settings } from './pages/Settings';
import { Reports } from './pages/Reports';
import { ReportsV2 } from './pages/ReportsV2';
import Releases from './pages/Releases';
import DataUploads from './pages/DataUploads';
import { CampaignDetails } from './pages/CampaignDetails';
import { CampaignBudget } from './pages/CampaignBudget';
import { CampaignInsights } from './pages/CampaignInsights';
import { Login } from './pages/Login';
import AdminUsers from './pages/AdminUsers';
import EnhancedAdminRoles from './pages/EnhancedAdminRoles';
import EnhancedRolesDemo from './pages/EnhancedRolesDemo';
import { ErrorBoundary } from './components/ErrorBoundary';
import { MonthlyBudgets } from './components/budget/MonthlyBudgets';

function App() {
  const { user, loading } = useAuth();
  const { } = useTheme(); // Initialize theme on app load

  // If not configured, we still allow the Login page to render; the
  // login will show informative errors from the mock client.

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Show suspension screen if user is suspended
  if (user?.isSuspended) {
    return <SuspensionCheck><div /></SuspensionCheck>;
  }

  return (
    <ErrorBoundary>
      <Router>
        <Toaster richColors position="bottom-right" closeButton toastOptions={{ duration: 2000 }} />
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/demo/roles" element={<EnhancedRolesDemo />} />
          
          {/* Protected routes */}
          <Route path="/*" element={
            user ? (
              <ProxyProvider>
                <Layout>
                  <Routes>
                  <Route path="/" element={<Dashboard />} />
                  <Route path="/contacts" element={<Contacts />} />
                  <Route path="/artists" element={<Artists />} />
                  <Route path="/releases" element={<Releases />} />
                  <Route path="/campaigns" element={<Campaigns />} />
                  <Route path="/meta/alignment" element={<MetaAlignment />} />
                  <Route path="/campaign/:campaignId" element={<CampaignDetails />} />
                  <Route path="/campaign/:campaignId/budget" element={<CampaignBudget />} />
                  <Route path="/campaign/:campaignId/insights" element={<CampaignInsights />} />
                  <Route path="/budget" element={<MonthlyBudgets />} />
                  <Route path="/insights" element={<div className="p-8"><h1 className="text-2xl font-bold">AI Insights</h1><p className="text-gray-600 mt-2">Global insights dashboard coming soon...</p></div>} />
                  <Route path="/reports" element={<Reports />} />
                  <Route path="/reports-v2" element={<ReportsV2 />} />
                  <Route path="/datauploads" element={<DataUploads />} />
                  <Route path="/settings" element={<Settings />} />
                  
                  {/* Admin routes */}
                  <Route path="/admin/users" element={<AdminUsers />} />
                  <Route path="/admin/roles" element={<EnhancedAdminRoles />} />
                  </Routes>
                </Layout>
              </ProxyProvider>
            ) : (
              <Login />
            )
          } />
        </Routes>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
